import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wind, Cloud, Droplet, Flame, Wrench, Truck, Shirt, ArrowRight, X, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import styles from './OrbeInteractivo.module.css';

const NODOS = [
  { id: 1, title: "Aspiradoras\nIndustriales", slug: "aspiracion-y-extraccion", icon: Wind, color: "#f59e0b" },
  { id: 2, title: "Extractores de\nHumo Industrial", slug: "aspiracion-y-extraccion", icon: Flame, color: "#ef4444" },
  { id: 3, title: "Fregadoras\nIndustriales", slug: "tratamiento-de-pisos", icon: Droplet, color: "#3b82f6" },
  { id: 4, title: "Equipos de\nVapor Industrial", slug: "tratamiento-de-pisos", icon: Cloud, color: "#10b981" },
  { id: 5, title: "Destapadoras\nde Tuberías", slug: "alta-presion", icon: Wrench, color: "#8b5cf6" },
  { id: 6, title: "Limpieza de\nMaquinaria Industrial", slug: "alta-presion", icon: Truck, color: "#f97316" },
  { id: 7, title: "Hidrolavadoras\nIndustriales", slug: "alta-presion", icon: Zap, color: "#0ea5e9" },
  { id: 8, title: "Equipos para\nLavandería Industrial", slug: "lavanderia", icon: Shirt, color: "#ec4899" }
];

const MACRO_INFO = {
  "aspiracion-y-extraccion": {
    titulo: "Aspiración y Extracción",
    desc: "Sistemas avanzados de recolección de polvo, humos y sólidos para mantener su planta segura y cumplir con normativas industriales.",
    imagenes: [
      "/imagenes/productos/202ds-1.webp",
      "/imagenes/productos/pro-1-1.webp",
      "/imagenes/productos/401dry-1.webp"
    ]
  },
  "tratamiento-de-pisos": {
    titulo: "Tratamiento de Pisos",
    desc: "Equipos automatizados para el fregado, pulido y limpieza profunda de todo tipo de superficies, garantizando un entorno impecable.",
    imagenes: [
      "/imagenes/productos/ps530-w22-uc-1.webp",
      "/imagenes/productos/ps460-w22-uc-1.webp",
      "/imagenes/productos/ps030-w14-ug-1.webp"
    ]
  },
  "alta-presion": {
    titulo: "Alta Presión",
    desc: "Hidrolavadoras de alta y súper alta presión para la remoción de incrustaciones severas, limpieza de tuberías y mantenimiento pesado.",
    imagenes: [
      "/imagenes/productos/hidrolavadora-electrica-nlb-103055e-1.webp",
      "/imagenes/productos/hidrolavadora-diesel-35175d-1.webp",
      "/imagenes/productos/hidrolavadora-annovi-reverberi-1.webp"
    ]
  },
  "lavanderia": {
    titulo: "Lavandería Industrial",
    desc: "Equipos completos para el lavado, secado y planchado institucional con barrera sanitaria y máxima eficiencia energética.",
    imagenes: [
      "/imagenes/productos/dls-28-1.webp",
      "/imagenes/productos/dtt-35-1.webp",
      "/imagenes/productos/hpw-10-1.webp"
    ]
  }
};

export const OrbeInteractivo = () => {
  const [rotationAngle, setRotationAngle] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [nodoActivo, setNodoActivo] = useState(null);
  const [displayNodo, setDisplayNodo] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    let rotationTimer;
    if (autoRotate) {
      rotationTimer = setInterval(() => {
        setRotationAngle(prev => (prev + 0.3) % 360);
      }, 50);
    }
    return () => clearInterval(rotationTimer);
  }, [autoRotate]);

  const handleNodoClick = (nodo) => {
    setDisplayNodo(nodo);
    setNodoActivo(nodo);
    setAutoRotate(false);
    setImgIndex(0);
    
    // Calcular ángulo para traer el nodo seleccionado al frente
    const nodeIndex = NODOS.findIndex(n => n.id === nodo.id);
    const targetAngle = (nodeIndex / NODOS.length) * 360;
    setRotationAngle(270 - targetAngle);
  };

  const resetVista = () => {
    setNodoActivo(null);
    setAutoRotate(true);
  };

  const nextImg = (e) => {
    e.stopPropagation();
    const current = nodoActivo || displayNodo;
    if (current && MACRO_INFO[current.slug]) {
      const max = MACRO_INFO[current.slug].imagenes.length;
      setImgIndex((prev) => (prev + 1) % max);
    }
  };

  const prevImg = (e) => {
    e.stopPropagation();
    const current = nodoActivo || displayNodo;
    if (current && MACRO_INFO[current.slug]) {
      const max = MACRO_INFO[current.slug].imagenes.length;
      setImgIndex((prev) => (prev - 1 + max) % max);
    }
  };

  const calcularPosicion = (index, total) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = window.innerWidth < 768 ? 160 : 210;
    const radian = (angle * Math.PI) / 180;

    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);
    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = 1;
    const scale = 1;

    return { x, y, angle, zIndex, opacity, scale };
  };

  return (
    <div className={styles.seccionContenedor}>
      <div className={`${styles.layoutDual} ${nodoActivo ? styles.vistaDividida : ''}`}>
        
        {/* Sistema Orbital */}
        <div className={styles.orbitalContenedor}>
          <div className={styles.orbitaBase}>
            
            {/* Núcleo Central */}
            <div 
              className={styles.nucleoCentro}
              style={{ cursor: nodoActivo ? 'pointer' : 'default' }}
              onClick={nodoActivo ? resetVista : undefined}
            >
              <span className={styles.textoNucleo}>Explora<br/>Categorías</span>
            </div>

            <div className={styles.anilloOrbita} />

            {/* Nodos Giratorios */}
            {NODOS.map((nodo, index) => {
              const pos = calcularPosicion(index, NODOS.length);
              const isActive = nodoActivo && nodoActivo.id === nodo.id;

              return (
                <div
                  key={nodo.id}
                  className={`${styles.nodo} ${isActive ? styles.nodoActivo : ''}`}
                  style={{
                    transform: `translate(${pos.x}px, ${pos.y}px) scale(${pos.scale})`,
                    zIndex: isActive ? 200 : pos.zIndex,
                    opacity: isActive ? 1 : pos.opacity,
                  }}
                  onClick={() => handleNodoClick(nodo)}
                >
                  <div 
                    className={styles.nodoCirculo}
                    style={{
                      borderColor: nodo.color,
                      boxShadow: isActive ? `0 0 20px ${nodo.color}` : 'none',
                      background: isActive ? nodo.color : 'rgba(10, 15, 20, 0.9)'
                    }}
                  >
                    <nodo.icon size={28} color={isActive ? '#000' : nodo.color} />
                    <span 
                      className={styles.nodoTexto}
                      style={{
                        color: isActive ? '#000' : '#fff',
                        textShadow: isActive ? 'none' : undefined
                      }}
                    >
                      {nodo.title}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Panel Derecho: Magno Categoría */}
        <div className={`${styles.panelMagno} ${nodoActivo ? styles.panelMagnoVisible : ''}`}>
          <button className={styles.btnCerrar} onClick={resetVista}>
            <X size={18} />
          </button>
          
          {displayNodo && MACRO_INFO[displayNodo.slug] && (
            <>
              <div className={styles.magnoImagenContenedor}>
                <button className={`${styles.flechaImg} ${styles.flechaImgIzquierda}`} onClick={prevImg}>
                  <ChevronLeft size={20} />
                </button>
                <img 
                  src={MACRO_INFO[displayNodo.slug].imagenes[imgIndex]} 
                  alt={MACRO_INFO[displayNodo.slug].titulo} 
                  className={styles.magnoImagen} 
                />
                <button className={`${styles.flechaImg} ${styles.flechaImgDerecha}`} onClick={nextImg}>
                  <ChevronRight size={20} />
                </button>
              </div>
              <span className={styles.magnoTag}>Solución Sugerida</span>
              <h3 className={styles.magnoTitulo}>{MACRO_INFO[displayNodo.slug].titulo}</h3>
              <p className={styles.magnoDesc}>{MACRO_INFO[displayNodo.slug].desc}</p>
              
              <Link 
                to={`/productos/macro/${displayNodo.slug}`} 
                className={styles.btnExplorar}
              >
                Explorar Equipos <ArrowRight size={18} />
              </Link>
            </>
          )}
        </div>

      </div>
    </div>
  );
};
